import React, { useState, useEffect } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UISectionTitle from '@/components/shared/UISectionTitle';
import UIButton from '@/components/shared/UIButton';
import TenantsListTable from '../TenantsListTable';
import UIPaginator from '@/components/shared/UIPaginator';
import './TenantsListSection.scss';

export interface TenantListData {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  paymentStatus: 'paid' | 'debtor' | 'not-set' | 'inactive' | 'pending' | 'active';
  property: string;
  unit: string;
}

export interface TenantsListSectionProps {
  title?: string;
  data?: TenantListData[];
  onTenantAction?: (tenantId: string, action: string) => void;
  onAddTenant?: () => void;
  loading?: boolean;
  searchTerm?: string;
  statusFilter?: string;
}

const baseClass = 'TenantsListSection';

const mockHandlers = {
  onTenantAction: (tenantId: string, action: string) => console.log('Tenant action:', tenantId, action),
  onAddTenant: () => console.log('Add new tenant')
};

const TenantsListSection: React.FC<TenantsListSectionProps> = ({
                                                                 title = 'List of tenants',
                                                                 data = [],
                                                                 onTenantAction = mockHandlers.onTenantAction,
                                                                 onAddTenant = mockHandlers.onAddTenant,
                                                                 loading = false,
                                                                 searchTerm = '',
                                                                 statusFilter = 'all',
                                                               }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Reset to the first page whenever the data or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [data, searchTerm, statusFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Paginate the data received from props
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageData = data.slice(startIndex, endIndex);



  return (
    <UISectionWrapper
      className={baseClass}
      align="stretch"
    >
      <UISectionHeader>
        <UISectionTitle
          title={title}
          action={null}
          showDivider={true}
        />
      </UISectionHeader>

      <UISectionBody>
        <div className={`${baseClass}__content`}>
          {loading ? (
            <div className={`${baseClass}__table-placeholder`}>
              Loading tenants...
            </div>
          ) : data.length === 0 ? (
            <div className={`${baseClass}__table-placeholder`}>
              {searchTerm || statusFilter !== 'all'
                ? 'No tenants found matching your search criteria.'
                : 'No tenants available.'
              }
            </div>
          ) : (
            <>
              <TenantsListTable
                data={currentPageData}
                onTenantAction={onTenantAction}
              />

              <UIPaginator
                currentPage={currentPage}
                totalPages={Math.ceil(data.length / itemsPerPage)}
                onPageChange={handlePageChange}
                className={`${baseClass}__navigation`}
              />
            </>
          )}
        </div>
      </UISectionBody>
    </UISectionWrapper>
  );
};

export default TenantsListSection;
